/**
 * Bet365-style category tile rail — controlled in-page filter.
 *
 * Horizontal snap-scroll row of tile cards (icon on top, label below)
 * rendered under the sort/toggle chips inside V6BrowseFilterPanel. A
 * tap calls `onSelect(slug | null)` so the panel's existing useMemo
 * filters the grid client-side — no page nav, no fetch.
 *
 * Renders on every breakpoint per founder direction 2026-05-24:
 * desktop users want the same fast in-page filter mobile gets. Mono
 * Lucide stroked icons per platform rule (no branded sport logos).
 */
"use client";

import {
  Crown,
  Trophy,
  Landmark,
  Bitcoin,
  TrendingUp,
  Globe,
  Clapperboard,
  FlaskConical,
  Zap,
  type LucideIcon,
} from "lucide-react";

type CategoryTile = {
  label: string;
  icon: LucideIcon;
  category: string | null;
};

const TILES: CategoryTile[] = [
  { label: "All", icon: Crown, category: null },
  { label: "Sports", icon: Trophy, category: "sports" },
  { label: "Politics", icon: Landmark, category: "politics" },
  { label: "Crypto", icon: Bitcoin, category: "crypto" },
  { label: "Finance", icon: TrendingUp, category: "economics" },
  { label: "Geopolitics", icon: Globe, category: "world" },
  { label: "Culture", icon: Clapperboard, category: "culture" },
  { label: "Science", icon: FlaskConical, category: "tech" },
  { label: "Tech", icon: Zap, category: "ai" },
];

export function V6MobileCategoryRail({
  active,
  onSelect,
  showOnDesktop = false,
}: {
  active: string | null;
  onSelect: (category: string | null) => void;
  /**
   * 2026-05-25 — on the dedicated /markets surface the founder wants
   * the full category set visible on desktop UNDER the filter chips,
   * not just on mobile. The landing page (/) keeps the rail mobile-
   * only because the v6 left sidebar already carries categories on
   * desktop there. Pass `showOnDesktop` from /markets only.
   */
  showOnDesktop?: boolean;
}) {
  return (
    <section
      className={`mx-auto mt-3 w-full max-w-[1280px] ${
        showOnDesktop ? "" : "sm:hidden"
      }`}
      aria-label="Browse markets by category"
    >
      <div
        className="flex snap-x snap-mandatory gap-1.5 overflow-x-auto px-4 pb-1 sm:flex-wrap sm:overflow-visible"
        style={{ scrollbarWidth: "none" }}
      >
        {TILES.map((tile) => {
          const Icon = tile.icon;
          const isActive = tile.category === active;
          return (
            <button
              key={tile.label}
              type="button"
              onClick={() => onSelect(tile.category)}
              aria-pressed={isActive}
              aria-label={`Filter by ${tile.label}`}
              className="flex w-[64px] flex-shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 transition-colors"
              style={{
                // 2026-05-25 founder direction: active tile takes the
                // v6 ochre tint instead of surface gray.
                background: isActive
                  ? "color-mix(in srgb, var(--color-accent) 14%, transparent)"
                  : "transparent",
                color: isActive ? "var(--color-text)" : "var(--color-text2)",
                minHeight: 56,
              }}
            >
              <Icon
                size={16}
                strokeWidth={1.75}
                aria-hidden="true"
                style={{
                  color: isActive
                    ? "var(--color-accent)"
                    : "var(--color-text3)",
                }}
              />
              <span
                className="text-[10px] leading-none"
                style={{
                  fontWeight: isActive ? 600 : 500,
                  letterSpacing: "-0.005em",
                }}
              >
                {tile.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
